import {
  CommentAuthorType,
  ContentType,
  InlineComment,
  UpdateInlineCommentsAttributes,
  UserCommentState,
  UserCommentType,
} from '@amzn/awscat-pattern-library-service-model';
import _ from 'lodash';
import { DateTime } from 'luxon';
import PQueue from 'p-queue';
import { v4 as uuid } from 'uuid';

import { IMplServiceClient } from '../api/IMplServiceClient';
import LocalStorage from '../common/LocalStorage';
import {
  categorizeCommentsByThread,
  categorizeInlineCommentsBySection,
} from '../components/utils/InlineCommentUtils';
import { log } from '../loggers/Logger';
import { CommentAnchor } from '../model/CommentAnchor';
import { AutoSavingService } from './AutoSavingService';
import {
  ContentAlreadyOpenError,
  NoContentOpenError,
  SaveCommentError,
  ServiceCallError,
} from './Errors';
import { ContentEditorService } from './ContentEditorService';
import { filterInlineComments, InlineTextFilter, NodeIdMappings } from './InlineTextOperations';
import { TypedEmitter, Unsubscribe } from './TypedEmitter';

/** Maximum number of comments that can be updated in one call. */
const MAX_UPDATE_COMMENTS_PER_REQUEST = 100;

/** Position update for a comment whose offsets shifted during editing. */
export interface CommentPositionUpdate {
  readonly commentId: string;
  readonly startOffset: number;
  readonly endOffset: number;
}

/**
 * THREAD MODEL (read this first)
 *
 * Every inline comment has a `commentId` (its own id) and a `threadId`
 * (pointer to the thread's root). The server represents the relationship as:
 *
 *   Thread root:  commentId = "abc",  threadId = ""      (empty / falsy)
 *   Reply:        commentId = "def",  threadId = "abc"   (points to root)
 *
 * So "threadId" in this service's API always means
 *   "the commentId of the thread's root comment."
 *
 * A reply is any comment whose `threadId` is truthy. A thread root is any
 * comment whose `threadId` is empty (see `isThreadRoot`). Some test fixtures
 * set the root's `threadId` to its own `commentId`; that's treated as a root.
 */

/**
 * Build an update holding only the fields that differ from the server, or
 * undefined when nothing changed.
 */
function buildChangedAttributes(
  local: InlineComment,
  server: InlineComment,
): UpdateInlineCommentsAttributes | undefined {
  // The backend checks permission per field it receives: only the author may
  // send `commentData`, and only editors/Admin/TechWriter may send offsets or
  // `textData`. Sending an unchanged field still counts, so a user whose edit
  // shifts someone else's comment gets a 403. Send only what changed.
  const changed: Omit<UpdateInlineCommentsAttributes, 'commentId'> = {
    ...(local.commentData !== server.commentData ? { commentData: local.commentData } : {}),
    ...(local.state !== server.state ? { state: local.state } : {}),
    ...(local.startOffset !== server.startOffset ? { startOffset: local.startOffset } : {}),
    ...(local.endOffset !== server.endOffset ? { endOffset: local.endOffset } : {}),
    ...(local.textData !== server.textData ? { textData: local.textData } : {}),
  };

  if (Object.keys(changed).length === 0) return undefined;
  return { commentId: local.commentId, ...changed };
}

/** `draft` for normal editing, `finalizing` for the tech writer's copy. */
export type CommentLoadOptions =
  | { readonly kind: 'draft'; readonly workflowId: number }
  | { readonly kind: 'finalizing'; readonly workflowId: number };

/** Snapshot of the currently-open comments for a single content. */
export interface OpenedComments {
  readonly contentId: string;
  readonly workflowId: number;
  readonly kind: CommentLoadOptions['kind'];
  readonly comments: readonly InlineComment[];
}

/** Events emitted by {@link InlineCommentManager}. */
export type InlineCommentManagerEvents = {
  /**
   * Fired after comment data or active thread changes. Specifically:
   *   - after `open` succeeds,
   *   - after `close`,
   *   - after any mutation (`createComment`, `updateComment`,
   *     `resolveThread`, `deleteComment`) completes its post-refresh,
   *   - after `refresh` finishes,
   *   - after thread selection changes.
   *
   * Does NOT fire on position drift ({@link stageCommentPositions}) or
   * view-commenting toggle ({@link toggleViewCommenting}).
   */
  changed: () => void;
  /** Fired when view-commenting mode is toggled on or off. */
  viewCommentingModeChanged: (enabled: boolean) => void;
  /** Fired when the sidebar should scroll to reveal the active thread. */
  revealInSidebarRequested: (threadId: string) => void;
  /** Fired when the editor should scroll to reveal the highlighted text. */
  revealInEditorRequested: (threadId: string) => void;
};

/**
 * Owns the inline-comment list for one opened content.
 *
 * Only one content open at a time. Every mutation hits the backend
 * immediately and then refreshes the snapshot. Subscribe with
 * `on('changed')` to re-read `openedComments`.
 *
 * Anchor drift during typing is handled separately by
 * {@link stageCommentPositions}.
 */
export class InlineCommentManager {
  private readonly _mpl: IMplServiceClient;
  private readonly _autoSaving: AutoSavingService;
  private readonly _emitter = new TypedEmitter<InlineCommentManagerEvents>();
  // This service keeps all comments in-memory and operates on them
  // synchronously. Use this queue to serialize backend updates.
  private readonly _queue = new PQueue({ concurrency: 1 });

  private _opened: OpenedComments | undefined = undefined;
  private _activeThreadId: string | null = null;
  private _nodeIdMappings: NodeIdMappings | undefined = undefined;
  private _isViewCommentingEnabled: boolean;
  private _snapshotVersion = 0;
  private readonly _unsubscribeContentEditor: Unsubscribe;
  // The last load request, kept so a failed load can be retried without the
  // caller re-supplying the contentId/options.
  private _pendingLoad:
    | { readonly contentId: string; readonly options: CommentLoadOptions }
    | undefined = undefined;
  private _loadError: Error | undefined = undefined;

  constructor(
    mpl: IMplServiceClient,
    autoSaving: AutoSavingService,
    contentEditor: ContentEditorService,
  ) {
    this._mpl = mpl;
    this._autoSaving = autoSaving;
    this._isViewCommentingEnabled = LocalStorage.getViewCommentingMode();

    this._nodeIdMappings = contentEditor.nodeIdMappings;
    this._unsubscribeContentEditor = contentEditor.on('changed', () => {
      const newMappings = contentEditor.nodeIdMappings;

      // Quick reference check — same object means nothing changed.
      if (newMappings === this._nodeIdMappings) return;

      // Deep compare — a new object with identical content won't affect filterComments results.
      if (_.isEqual(newMappings, this._nodeIdMappings)) {
        this._nodeIdMappings = newMappings;
        return;
      }

      // Mappings structurally changed — filterComments will return different results, notify callers.
      this._nodeIdMappings = newMappings;
      this._emitter.emit('changed');
    });
  }

  dispose(): void {
    this._unsubscribeContentEditor();
  }

  /** Whether commenting mode is enabled in viewing mode. */
  get isViewCommentingEnabled(): boolean {
    return this._isViewCommentingEnabled;
  }

  /** Toggle commenting mode on or off and persist to local storage. */
  toggleViewCommenting(enabled: boolean): void {
    if (enabled === this._isViewCommentingEnabled) return;
    this._isViewCommentingEnabled = enabled;
    LocalStorage.setViewCommentingMode(enabled);
    this._emitter.emit('viewCommentingModeChanged', enabled);
  }

  /** Currently-open comments, or undefined. */
  get openedComments(): OpenedComments | undefined {
    return this._opened;
  }

  /** The error from the last failed load, or undefined when the load succeeded or none was attempted. */
  get loadError(): Error | undefined {
    return this._loadError;
  }

  /** Returns opened comments matching the filter, or empty if nothing is open. */
  filterComments(filter: InlineTextFilter): readonly InlineComment[] {
    if (!this._opened) return [];
    const mappings = this._nodeIdMappings;
    if (!mappings) return [];
    return filterInlineComments(filter, mappings, this._opened.comments);
  }

  /** Currently-selected thread root commentId, or null if none selected. */
  get activeThreadId(): string | null {
    return this._activeThreadId;
  }

  /** Select a thread by its root commentId, or pass null to deselect. */
  selectThread(threadId: string | null): void {
    if (threadId === this._activeThreadId) return;
    this._activeThreadId = threadId;
    this._emitter.emit('changed');
  }

  /** Select a thread and request the sidebar to scroll it into view. */
  revealInSidebar(threadId: string): void {
    this._activeThreadId = threadId;
    this._emitter.emit('changed');
    this._emitter.emit('revealInSidebarRequested', threadId);
  }

  /** Select a thread and request the editor to scroll the highlighted text into view. */
  revealInEditor(threadId: string): void {
    this._activeThreadId = threadId;
    this._emitter.emit('changed');
    this._emitter.emit('revealInEditorRequested', threadId);
  }

  /**
   * Load inline comments for `contentId`.
   *
   * A failed fetch does not reject — it is captured in {@link loadError} so the
   * comments panel can offer a retry instead of spinning forever. Retry with
   * {@link retryLoad}.
   *
   * @throws {ContentAlreadyOpenError} if another content is already open.
   */
  async open(contentId: string, options: CommentLoadOptions): Promise<void> {
    await this._queue.add(() => this.doOpen(contentId, options));
  }

  /**
   * Re-attempt the last {@link open} after it failed. Resolves without doing
   * anything when the last load already succeeded or none was attempted.
   */
  async retryLoad(): Promise<void> {
    await this._queue.add(() => this.doRetryLoad());
  }

  /**
   * Re-fetch the comment list.
   *
   * @throws {NoContentOpenError} if no content is open.
   * @throws {ServiceCallError} when the backend call fails
   */
  async refresh(): Promise<void> {
    const versionAtRequestTime = this._snapshotVersion;
    await this._queue.add(() => this.doRefresh(versionAtRequestTime));
  }

  /**
   * Drop the open snapshot. Waits for any in-flight mutation to finish
   * first so the service ends up in a clean closed state. Safe when
   * nothing is open.
   */
  async close(): Promise<void> {
    await this._queue.add(() => this.doClose());
  }

  /** Group open comments by section. */
  getBySection(contentType: ContentType): ReturnType<typeof categorizeInlineCommentsBySection> {
    const opened = this._opened;
    if (!opened) return [];
    return categorizeInlineCommentsBySection([...opened.comments], contentType);
  }

  /** Group open comments by thread (replies under their root). */
  getByThread(): ReturnType<typeof categorizeCommentsByThread> {
    const opened = this._opened;
    if (!opened) return [];
    return categorizeCommentsByThread([...opened.comments]);
  }

  /**
   * Create a comment.
   *
   * - `new-thread`: user selected text and wrote the first comment.
   * - `reply`: response to an existing thread. `threadId` is the root's
   *   `commentId`; path/anchor are inherited from the root.
   *
   * @throws {NoContentOpenError} if no content is open.
   * @throws {Error} on reply when no comment matching `threadId` exists in
   *   the open snapshot.
   * @throws {ServiceCallError} when the backend call fails
   */
  async createComment(
    args:
      | {
          readonly kind: 'new-thread';
          readonly sectionPath: string[];
          readonly anchor: CommentAnchor;
          readonly body: string;
        }
      | {
          readonly kind: 'reply';
          /** The root's `commentId`. */
          readonly threadId: string;
          readonly body: string;
        },
  ): Promise<string> {
    const opened = this.requireOpen('createComment');

    // Enqueue the API call before optimistic update so listeners
    // reacting to 'changed' can't sneak a refresh() ahead of it.
    const createTask = this._queue.add(() => this.doCreateComment(args)) as Promise<string>;

    const placeholder =
      args.kind === 'new-thread'
        ? buildOptimisticNewThread(args.sectionPath, args.anchor, args.body)
        : buildOptimisticReply(this.findThreadRoot(args.threadId), args.body);

    if (placeholder) {
      this._opened = { ...opened, comments: [...opened.comments, placeholder] };
      this._snapshotVersion++;
      this._emitter.emit('changed');
    }

    try {
      return await createTask;
    } catch (error) {
      if (placeholder) {
        this.revertOptimisticCreate(placeholder.commentId);
      }
      throw error;
    }
  }

  /**
   * Edit a comment's body.
   *
   * State changes (resolve/unresolve) go through {@link resolveThread} —
   * not here — so cascading to replies stays explicit. Anchor writes are
   * not a user-initiated operation: drift-during-typing is handled by
   * {@link stageCommentPositions}
   *
   * @throws {NoContentOpenError} if no content is open.
   * @throws {ServiceCallError} when the backend call fails
   */
  async updateComment(commentId: string, body: string): Promise<void> {
    const opened = this.requireOpen('updateComment');

    // Capture originals before mutation for rollback.
    const originals = opened.comments.filter((c) => c.commentId === commentId);

    // Enqueue the API call before optimistic update so listeners
    // reacting to 'changed' can't sneak a refresh() ahead of it.
    const updateTask = this._queue.add(() => this.doUpdateComment(opened, commentId, body));

    this.applyOptimisticUpdates(originals.map((c) => ({ ...c, commentData: body })));

    try {
      await updateTask;
    } catch (error) {
      this.revertOptimisticMutations(originals);
      throw error;
    }
  }

  /**
   * Stages position updates (offset drift from editing) without saving immediately.
   * Persists to the working copy for crash recovery; saved to server by {@link saveComments}.
   *
   * Does not emit 'changed' — position drift is invisible to subscribers.
   *
   * @throws {NoContentOpenError} if no content is open.
   */
  stageCommentPositions(updates: readonly CommentPositionUpdate[]): void {
    if (updates.length === 0) return;
    const opened = this.requireOpen('stageCommentPositions');

    // Index updates by commentId for O(1) lookup during merge.
    const updateMap = new Map(updates.map((u) => [u.commentId, u]));

    // Merge each partial update into the existing snapshot.
    const mergedComments: InlineComment[] = [];
    for (const existing of opened.comments) {
      const update = updateMap.get(existing.commentId);
      if (!update) {
        mergedComments.push(existing);
        continue;
      }
      mergedComments.push({
        ...existing,
        startOffset: update.startOffset,
        endOffset: update.endOffset,
      } as InlineComment);
      updateMap.delete(existing.commentId);
    }

    // Log any updates that didn't match an existing comment.
    for (const [commentId] of updateMap) {
      log.error(
        `[stageCommentPositions] comment "${commentId}" not found in opened snapshot; skipping`,
      );
    }

    this._opened = { ...opened, comments: mergedComments };

    // Persist merged full objects to working copy for crash recovery.
    const stagedFull = mergedComments.filter((c) =>
      updates.some((u) => u.commentId === c.commentId),
    );
    this._autoSaving.stageUpdatedComments(stagedFull);

    // Position drift does not emit 'changed' — no subscriber needs to re-render on offset shifts.
  }

  /**
   * Resolve or unresolve a thread.
   *
   * Pass any comment id in the thread — the service finds the root and
   * updates the root plus every reply in one batch. This matches the UI
   * rule: flipping the toggle on any thread member resolves the whole
   * thread.
   *
   * @throws {NoContentOpenError} if no content is open.
   * @throws {Error} if no comment matching `commentIdInThread` exists in
   *   the open snapshot.
   * @throws {ServiceCallError} when the backend call fails
   */
  async resolveThread(commentIdInThread: string, isResolved: boolean): Promise<void> {
    const opened = this.requireOpen('resolveThread');

    const root = this.findThreadRoot(commentIdInThread);
    if (!root) {
      throw new Error(
        `Cannot resolve: no thread found for commentId "${commentIdInThread}" in the open comment list.`,
      );
    }

    const newState = isResolved ? UserCommentState.RESOLVED : UserCommentState.PENDING;

    // Take a snapshot of the original state so we can revert them when things go wrong
    const originals = opened.comments.filter((c) => c.commentId === root.commentId);

    // Enqueue the API call before optimistic update so listeners
    // reacting to 'changed' can't sneak a refresh() ahead of it.
    const updateTask = this._queue.add(() => this.doResolveThread(opened, root, newState));

    this.applyOptimisticUpdates(originals.map((c) => ({ ...c, state: newState })));

    try {
      await updateTask;
    } catch (error) {
      this.revertOptimisticMutations(originals);
      throw error;
    }
  }

  /**
   * Resolve a thread with a specific terminal state (IMPLEMENTED, IGNORED, etc.).
   *
   * @throws {NoContentOpenError} if no content is open.
   * @throws {Error} if no comment matching `commentIdInThread` exists in the open snapshot.
   * @throws {ServiceCallError} when the backend call fails
   */
  async resolveThreadAs(commentIdInThread: string, state: UserCommentState): Promise<void> {
    const opened = this.requireOpen('resolveThreadAs');

    const root = this.findThreadRoot(commentIdInThread);
    if (!root) {
      throw new Error(
        `Cannot resolve: no thread found for commentId "${commentIdInThread}" in the open comment list.`,
      );
    }

    // Only the root is mutated locally; the backend cascades resolution to
    // replies, and mirroring that here would over-report state until the
    // next full load.
    const originals = opened.comments.filter((c) => c.commentId === root.commentId);

    // Enqueue the API call before optimistic update so listeners
    // reacting to 'changed' can't sneak a refresh() ahead of it.
    const updateTask = this._queue.add(() => this.doResolveThread(opened, root, state));

    this.applyOptimisticUpdates(originals.map((c) => ({ ...c, state })));

    try {
      await updateTask;
    } catch (error) {
      this.revertOptimisticMutations(originals);
      throw error;
    }
  }

  /**
   * Delete a comment. Deleting a root deletes the whole thread server-side.
   *
   * @throws {NoContentOpenError} if no content is open.
   * @throws {ServiceCallError} when the backend call fails
   */
  async deleteComment(commentId: string): Promise<void> {
    const opened = this.requireOpen('deleteComment');

    // Enqueue the API call before optimistic update so listeners
    // reacting to 'changed' can't sneak a refresh() ahead of it.
    const updateTask = this._queue.add(() => this.doDeleteComment(commentId));

    // Capture removed comments and active thread for rollback.
    const isRoot = opened.comments.some((c) => c.commentId === commentId && isThreadRoot(c));
    const removed = opened.comments.filter((c) =>
      isRoot ? c.commentId === commentId || c.threadId === commentId : c.commentId === commentId,
    );
    const previousActiveThreadId = this._activeThreadId;

    this._opened = {
      ...opened,
      comments: opened.comments.filter((c) => !removed.some((r) => r.commentId === c.commentId)),
    };
    this._snapshotVersion++;
    if (commentId === this._activeThreadId) {
      this._activeThreadId = null;
    }
    this._emitter.emit('changed');

    try {
      await updateTask;
    } catch (error) {
      this.revertOptimisticDelete(removed, previousActiveThreadId);
      throw error;
    }
  }

  /**
   * Resolve all unresolved scanner-authored comments in a single batch API call.
   *
   * @throws {NoContentOpenError} if no content is open.
   * @throws {ServiceCallError} when the backend call fails
   */
  async resolveAllScanComments(commentIds: readonly string[]): Promise<void> {
    if (commentIds.length === 0) return;
    const opened = this.requireOpen('resolveAllScanComments');

    // Capture originals before mutation so a failed chunk can roll back only its own comments.
    const idSet = new Set(commentIds);
    const originalById = new Map(
      opened.comments.filter((c) => idSet.has(c.commentId)).map((c) => [c.commentId, c]),
    );

    // Run the whole batch as one queued task so other work can't interleave between chunks.
    await this._queue.add(async () => {
      for (const chunkedCommentIds of _.chunk(commentIds, MAX_UPDATE_COMMENTS_PER_REQUEST)) {
        const chunkOriginals = chunkedCommentIds
          .map((id) => originalById.get(id))
          .filter((c): c is InlineComment => !!c);

        this.applyOptimisticUpdates(
          chunkOriginals.map((c) => ({ ...c, state: UserCommentState.IMPLEMENTED })),
        );

        const attrs = chunkedCommentIds.map((commentId) => ({
          commentId,
          state: UserCommentState.IMPLEMENTED,
        })) as UpdateInlineCommentsAttributes[];

        try {
          await this._mpl.updateInlineComments(opened.contentId, opened.workflowId, attrs);
        } catch (error) {
          // Only roll back the chunk that failed; earlier chunks already landed server-side.
          this.revertOptimisticMutations(chunkOriginals);
          throw error;
        }
      }

      await this.doRefresh(this._snapshotVersion);
    });
  }

  /**
   * Persist staged comment changes to the server.
   *
   * Fetches the server's current list, diffs all properties against the
   * in-memory snapshot, and only sends updates for comments that genuinely
   * changed. No-ops when nothing changed.
   *
   * @throws {NoContentOpenError} if no content is open.
   * @throws {SaveCommentError} if the server request to persist comments fails.
   */
  async saveComments(): Promise<void> {
    await this._queue.add(() => this.doSaveComments());
  }

  /** Subscribe to an event. See {@link InlineCommentManagerEvents}. */
  on<K extends keyof InlineCommentManagerEvents>(
    event: K,
    listener: InlineCommentManagerEvents[K],
  ): Unsubscribe {
    return this._emitter.on(event, listener);
  }

  private async doOpen(contentId: string, options: CommentLoadOptions): Promise<void> {
    if (this._opened) {
      throw new ContentAlreadyOpenError(this._opened.contentId, contentId);
    }
    // Remember the request so a failed load can be retried without the caller.
    this._pendingLoad = { contentId, options };
    await this.loadPending();
  }

  private async doRetryLoad(): Promise<void> {
    // Nothing to retry when no load was attempted or the last one succeeded.
    if (!this._pendingLoad || this._opened) return;
    await this.loadPending();
  }

  /**
   * Fetch the {@link _pendingLoad} request and populate the snapshot. A failed
   * fetch is captured in {@link _loadError} rather than thrown, so the panel
   * exits its loading state and can offer a retry. Always emits 'changed'.
   */
  private async loadPending(): Promise<void> {
    const request = this._pendingLoad;
    if (!request) return;
    const { contentId, options } = request;

    // Clear any previous error and re-enter the loading state so the panel
    // shows the spinner (and hides the Retry button) while the fetch is in
    // flight — important on retry, where an error is already on screen.
    this._loadError = undefined;
    this._emitter.emit('changed');

    try {
      // Panel-owned load: the panel reports any failure via its own error + retry.
      let comments = await this.fetchComments(contentId, options);

      // Apply unsaved edits from the working copy. AutoSavingService may hold
      // positions that the server doesn't know about yet (e.g. user closed the
      // tab mid-edit and the working copy was restored on reopen).
      const pending = this._autoSaving.pendingEdits?.updatedComments;
      if (pending && pending.size > 0) {
        comments = comments.map((c) => pending.get(c.commentId) ?? c);
      }

      this._opened = {
        contentId,
        workflowId: options.workflowId,
        kind: options.kind,
        comments,
      };
    } catch (error) {
      this._loadError = error instanceof Error ? error : new Error(String(error));
      log.error({ error, contentId }, 'Failed to load inline comments');
    }

    this._emitter.emit('changed');
  }

  private async doClose(): Promise<void> {
    const hadError = this._loadError !== undefined;
    this._pendingLoad = undefined;
    this._loadError = undefined;
    // Nothing open and no error to clear — already in a clean closed state.
    if (this._opened === undefined && !hadError) return;
    this._opened = undefined;
    this._activeThreadId = null;
    this._emitter.emit('changed');
  }

  /**
   * @throws When the snapshot keeps changing across retries, indicating a
   *   server fetch error or concurrent user interaction preventing the
   *   refresh from settling (very unlikely).
   * @throws {ServiceCallError} when the backend call fails
   */
  private async doRefresh(versionAtRequestTime: number, retries = 0): Promise<void> {
    const current = this.requireOpen('refresh');
    const comments = await this.fetchComments(current.contentId, {
      kind: current.kind,
      workflowId: current.workflowId,
    });
    if (this._snapshotVersion !== versionAtRequestTime) {
      if (retries >= 6) {
        throw new Error(
          'Failed to refresh comments: snapshot version kept changing across retries.',
        );
      }
      return this.doRefresh(this._snapshotVersion, retries + 1);
    }
    this._opened = { ...current, comments };
    this._emitter.emit('changed');
  }

  /** @throws {ServiceCallError} when the backend call fails */
  private async doCreateComment(
    args:
      | {
          readonly kind: 'new-thread';
          readonly sectionPath: string[];
          readonly anchor: CommentAnchor;
          readonly body: string;
        }
      | {
          readonly kind: 'reply';
          readonly threadId: string;
          readonly body: string;
        },
  ): Promise<string> {
    const opened = this.requireOpen('createComment');
    let response;
    if (args.kind === 'new-thread') {
      response = await this._mpl.createInlineComment(
        args.sectionPath,
        opened.contentId,
        opened.workflowId,
        UserCommentType.Thread,
        args.body,
        args.anchor.text,
        '',
        args.anchor.startOffset,
        args.anchor.endOffset,
      );
    } else {
      // Reply anchoring must come from the thread root, not whichever
      // comment the caller happened to pass. All replies share the root's
      // path/anchor.
      const root = this.findThreadRoot(args.threadId);
      if (!root) {
        throw new Error(
          `Cannot reply: no thread found for threadId "${args.threadId}" in the open comment list.`,
        );
      }
      response = await this._mpl.createInlineComment(
        root.path,
        opened.contentId,
        opened.workflowId,
        UserCommentType.Reply,
        args.body,
        root.textData,
        root.commentId,
        root.startOffset,
        root.endOffset,
      );
    }
    await this.doRefresh(this._snapshotVersion);
    return response.commentId;
  }

  /** @throws {ServiceCallError} when the backend call fails */
  private async doUpdateComment(
    opened: OpenedComments,
    commentId: string,
    body: string,
  ): Promise<void> {
    const attr: UpdateInlineCommentsAttributes = {
      commentId,
      commentData: body,
    } as UpdateInlineCommentsAttributes;
    await this._mpl.updateInlineComments(opened.contentId, opened.workflowId, [attr]);
  }

  /** @throws {ServiceCallError} when the backend call fails */
  private async doResolveThread(
    opened: OpenedComments,
    root: InlineComment,
    state: UserCommentState,
  ): Promise<void> {
    const attrs: UpdateInlineCommentsAttributes[] = [
      { commentId: root.commentId, state } as UpdateInlineCommentsAttributes,
    ];
    await this._mpl.updateInlineComments(opened.contentId, opened.workflowId, attrs);
  }

  /** @throws {ServiceCallError} when the backend call fails */
  private async doDeleteComment(commentId: string): Promise<void> {
    await this._mpl.deleteInlineComment(commentId);
    await this.doRefresh(this._snapshotVersion);
  }

  /** Apply updated comments to the in-memory snapshot and emit immediately. */
  private applyOptimisticUpdates(updated: readonly InlineComment[]): void {
    const opened = this._opened;
    if (!opened) return;

    const updateMap = new Map(updated.map((c) => [c.commentId, c]));
    this._opened = {
      ...opened,
      comments: opened.comments.map((c) => updateMap.get(c.commentId) ?? c),
    };
    this._snapshotVersion++;

    // Keep autosaving's working copy in sync for crash recovery.
    const pending = this._autoSaving.pendingEdits?.updatedComments;
    if (pending) {
      const overlap = updated.filter((c) => pending.has(c.commentId));
      if (overlap.length > 0) {
        this._autoSaving.stageUpdatedComments(overlap);
      }
    }

    this._emitter.emit('changed');
  }

  /** Revert an optimistic create by removing the placeholder from the snapshot. */
  private revertOptimisticCreate(placeholderCommentId: string): void {
    const opened = this._opened;
    if (!opened) return;

    this._opened = {
      ...opened,
      comments: opened.comments.filter((c) => c.commentId !== placeholderCommentId),
    };
    this._snapshotVersion++;
    this._emitter.emit('changed');
  }

  /** Revert optimistic mutations by restoring original comment objects. */
  private revertOptimisticMutations(originals: readonly InlineComment[]): void {
    const opened = this._opened;
    if (!opened) return;

    const originalMap = new Map(originals.map((c) => [c.commentId, c]));
    this._opened = {
      ...opened,
      comments: opened.comments.map((c) => originalMap.get(c.commentId) ?? c),
    };
    this._snapshotVersion++;
    this._emitter.emit('changed');
  }

  /** Revert an optimistic delete by re-inserting removed comments. */
  private revertOptimisticDelete(
    removed: readonly InlineComment[],
    previousActiveThreadId: string | null,
  ): void {
    const opened = this._opened;
    if (!opened) return;

    this._opened = { ...opened, comments: [...opened.comments, ...removed] };
    this._activeThreadId = previousActiveThreadId;
    this._snapshotVersion++;
    this._emitter.emit('changed');
  }

  private requireOpen(method: string): OpenedComments {
    if (!this._opened) throw new NoContentOpenError(method);
    return this._opened;
  }

  /**
   * Resolve any `commentId` in a thread to the thread's root comment.
   * Returns undefined when `commentIdInThread` doesn't match any comment
   * in the open snapshot.
   */
  private findThreadRoot(commentIdInThread: string): InlineComment | undefined {
    const opened = this._opened;
    if (!opened) return undefined;
    const target = opened.comments.find((c) => c.commentId === commentIdInThread);
    if (!target) return undefined;
    if (isThreadRoot(target)) return target;
    return opened.comments.find((c) => c.commentId === target.threadId);
  }

  /**
   * Fetch the comment list; callers surface any failure themselves.
   *
   * @throws {ServiceCallError} when the backend call fails
   */
  private async fetchComments(
    contentId: string,
    options: CommentLoadOptions,
  ): Promise<readonly InlineComment[]> {
    const { inlineComments } = await this._mpl.listInlineComments(contentId, options.workflowId);
    return inlineComments;
  }

  /** @throws {SaveCommentError} if the server request to persist comments fails. */
  private async doSaveComments(): Promise<void> {
    const opened = this.requireOpen('saveComments');

    // Fetch the server's authoritative list to diff against.
    let serverComments;
    try {
      serverComments = await this.fetchComments(opened.contentId, {
        kind: opened.kind,
        workflowId: opened.workflowId,
      });
    } catch (err) {
      throw err instanceof ServiceCallError
        ? SaveCommentError.fromServiceCall(err)
        : new SaveCommentError(err);
    }
    const serverMap = new Map(serverComments.map((c) => [c.commentId, c]));

    // Build update batch: only comments that differ from the server's version,
    // each carrying only its changed fields.
    const attrs: UpdateInlineCommentsAttributes[] = [];
    for (const local of opened.comments) {
      const server = serverMap.get(local.commentId);
      if (!server) continue;
      const changed = buildChangedAttributes(local, server);
      if (changed) attrs.push(changed);
    }

    if (attrs.length > 0) {
      try {
        await this._mpl.updateInlineComments(opened.contentId, opened.workflowId, attrs);
      } catch (err) {
        throw err instanceof ServiceCallError
          ? SaveCommentError.fromServiceCall(err)
          : new SaveCommentError(err);
      }
    }
  }
}

/**
 * A comment is a thread root when it has no parent pointer. MPL represents
 * "no parent" as empty string, null, or (in some fixtures) the comment's
 * own id.
 */
export function isThreadRoot(c: InlineComment): boolean {
  return !c.threadId || c.threadId === c.commentId;
}

export function buildOptimisticNewThread(
  sectionPath: string[],
  anchor: CommentAnchor,
  body: string,
): InlineComment {
  return {
    commentId: `TEMP_${uuid()}`,
    path: sectionPath,
    threadId: '',
    commentData: body,
    textData: anchor.text,
    state: UserCommentState.PENDING,
    createdAt: DateTime.utc(),
    startOffset: anchor.startOffset,
    endOffset: anchor.endOffset,
    principalId: '',
    principalName: '',
    finalizingStartOffset: 0,
    finalizingEndOffset: 0,
    finalizingTextData: '',
    authorType: CommentAuthorType.HUMAN,
  } as InlineComment;
}

/** A temporary id prefix we give newly added comments until they are synced with backend */
const OPTIMISTIC_REPLY_COMMENT_PREFIX = 'TEMP_';

export function buildOptimisticReply(
  root: InlineComment | undefined,
  body: string,
): InlineComment | undefined {
  if (!root) return undefined;
  return {
    commentId: `${OPTIMISTIC_REPLY_COMMENT_PREFIX}${uuid()}`,
    path: root.path,
    threadId: root.commentId,
    commentData: body,
    textData: root.textData,
    state: root.state,
    createdAt: DateTime.utc(),
    startOffset: root.startOffset,
    endOffset: root.endOffset,
    principalId: '',
    principalName: '',
    finalizingStartOffset: root.finalizingStartOffset,
    finalizingEndOffset: root.finalizingEndOffset,
    finalizingTextData: root.finalizingTextData,
    authorType: CommentAuthorType.HUMAN,
  } as InlineComment;
}

export function isOptimisticReply(comment: InlineComment) {
  return comment.commentId.startsWith(OPTIMISTIC_REPLY_COMMENT_PREFIX);
}
